import React from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  ANALYTICS_SERIES_COLORS,
  buildDistributionSummary,
  buildPieLabelRenderer,
  DistributionTooltip,
  formatDateValue,
  formatNumber,
  formatPercentage,
  formatRangeLabel,
  getKpiValue,
  getPeakEntry,
  getRangeDayCount,
  getTopItem,
  TimeSeriesTooltip,
} from "./shared/analyticsUtils";

const AREA_STROKE = "#2563EB";
const AREA_FILL = "#60A5FA";
const ACCOUNT_PIE_LABEL_RENDERER = buildPieLabelRenderer({ showLabel: false, minPercent: 0.08, fontSize: 12 });

function AccountsSection(props) {
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const rangeDayCount = getRangeDayCount(currentStartDate, currentEndDate);
  const registrationTrend = data?.registration_trend || [];
  const platformBreakdown = data?.platform_breakdown || [];
  const appointmentConversion = data?.appointment_conversion || [];
  const newAccounts = getKpiValue(data?.kpis, "new_accounts");
  const totalAccounts = getKpiValue(data?.kpis, "total_accounts");
  const activeAccounts = getKpiValue(data?.kpis, "active_accounts");
  const bookingRatio = getKpiValue(data?.kpis, "booking_ratio");
  const averageDaily = rangeDayCount > 0 ? Math.round(newAccounts / rangeDayCount) : 0;
  const topPlatform = getTopItem(platformBreakdown);
  const peakDay = getPeakEntry(registrationTrend);
  const loadingLabel = labels.loading || "...";

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.newRegistrations,
      value: loading ? loadingLabel : formatNumber(newAccounts, locale),
      hint: labels.registrationsUnit,
    },
    {
      label: labels.activeAccounts,
      value: loading ? loadingLabel : formatNumber(activeAccounts, locale),
      hint: labels.accountsUnit,
    },
    {
      label: labels.topPlatform,
      value: loading ? loadingLabel : topPlatform?.label || labels.noData,
      hint: topPlatform ? `${formatNumber(topPlatform.value, locale)} ${labels.registrationsUnit}` : null,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.totalAccounts,
      value: loading ? loadingLabel : formatNumber(totalAccounts, locale),
      hint: labels.accountsUnit,
    },
    {
      label: labels.bookingRatio,
      value: loading ? loadingLabel : formatPercentage(bookingRatio, locale),
      hint: labels.conversionHint,
    },
    {
      label: labels.averageDaily,
      value: loading ? loadingLabel : formatNumber(averageDaily, locale),
      hint: labels.registrationsUnit,
    },
  ];

  const trendItems = [
    {
      label: labels.peakDay,
      value: peakDay ? formatNumber(peakDay.count, locale) : labels.noData,
      hint: peakDay ? formatDateValue(peakDay.date, locale, { day: "2-digit", month: "short", year: "numeric" }) : null,
    },
    {
      label: labels.averageDaily,
      value: formatNumber(averageDaily, locale),
      hint: labels.registrationsUnit,
    },
    {
      label: labels.activeAccounts,
      value: formatNumber(activeAccounts, locale),
      hint: labels.accountsUnit,
    },
  ];

  const platformSummary = buildDistributionSummary(platformBreakdown, locale, labels.registrationsUnit);
  const conversionSummary = buildDistributionSummary(appointmentConversion, locale, labels.accountsUnit);

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
      />

      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={labels.registrationTrend}
            description={labels.registrationTrendDescription}
            loading={loading}
            isEmpty={!registrationTrend.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-account-add"
            aside={<AnalyticsMetricList items={trendItems} emptyLabel={labels.noData} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={registrationTrend} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="analytics-accounts-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={AREA_FILL} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={AREA_FILL} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.28)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDateValue(value, locale, { day: "2-digit", month: "short" })}
                  minTickGap={24}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<TimeSeriesTooltip locale={locale} unitLabel={labels.registrationsUnit} />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={AREA_STROKE}
                  strokeWidth={3}
                  fill="url(#analytics-accounts-area)"
                  fillOpacity={1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-6 mb-4">
          <AnalyticsChart
            title={labels.platformBreakdown}
            description={labels.platformBreakdownDescription}
            loading={loading}
            isEmpty={!platformBreakdown.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-smartphone"
            footer={<AnalyticsMetricList items={platformSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={platformBreakdown}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={132}
                  paddingAngle={0}
                  stroke="#fff"
                  strokeWidth={2}
                  labelLine={false}
                  label={ACCOUNT_PIE_LABEL_RENDERER}
                >
                  {platformBreakdown.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.registrationsUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>

        <div className="col-xl-6 mb-4">
          <AnalyticsChart
            title={labels.appointmentConversion}
            description={labels.appointmentConversionDescription}
            loading={loading}
            isEmpty={!appointmentConversion.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-calendar-check"
            footer={<AnalyticsMetricList items={conversionSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={appointmentConversion}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={132}
                  paddingAngle={0}
                  stroke="#fff"
                  strokeWidth={2}
                  labelLine={false}
                  label={ACCOUNT_PIE_LABEL_RENDERER}
                >
                  {appointmentConversion.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.accountsUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>
    </div>
  );
}

export default AccountsSection;
